import { useEffect, useRef, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import type { RecentMainCommandChat } from '../lib/main-command-chat-archive';

export const MAX_RECENT_MAIN_COMMAND_TABS = 5;
export type MainCommandOrganizerTool = 'attention' | 'archive' | null;

export function boundedRecentConversationTabs(
  items: readonly RecentMainCommandChat[],
): readonly RecentMainCommandChat[] {
  return items.slice(0, MAX_RECENT_MAIN_COMMAND_TABS);
}

export interface MainCommandConversationTabsProps {
  readonly items: readonly RecentMainCommandChat[];
  readonly archiveItems: readonly RecentMainCommandChat[];
  readonly selectedArchiveId: string | null;
  readonly unreadCount?: number;
  readonly attentionCount?: number;
  readonly canStartNew: boolean;
  readonly busy?: boolean;
  readonly activeTool: MainCommandOrganizerTool;
  readonly managementAttention?: ReactNode;
  readonly onSelect: (item: RecentMainCommandChat) => void;
  readonly onNewChat: () => void;
  readonly onTool: (tool: MainCommandOrganizerTool) => void;
}

export function MainCommandConversationTabs({
  items,
  archiveItems,
  selectedArchiveId,
  unreadCount = 0,
  attentionCount = 0,
  canStartNew,
  busy = false,
  activeTool,
  managementAttention,
  onSelect,
  onNewChat,
  onTool,
}: MainCommandConversationTabsProps): ReactElement {
  const recent = boundedRecentConversationTabs(items);
  const archived = archiveItems.filter((item) => item.kind === 'archived');
  const toolPanelRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (activeTool !== null) toolPanelRef.current?.focus();
  }, [activeTool]);

  const selectConversation = (item: RecentMainCommandChat) => {
    onTool(null);
    onSelect(item);
  };

  const selectByKeyboard = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (recent.length < 2) return;
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % recent.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + recent.length) % recent.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = recent.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const next = recent[nextIndex];
    if (!next) return;
    selectConversation(next);
    event.currentTarget
      .closest('.command-conversation-tabs')
      ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      ?.[nextIndex]
      ?.focus();
  };

  return (
    <section className="command-conversation-organizer" aria-label="Main Command views">
      <div className="command-conversation-strip">
        <div
          className="command-conversation-tabs"
          role="tablist"
          aria-label="Recent Main Command conversations"
          aria-orientation="horizontal"
        >
          {recent.map((item, index) => {
            const conversationSelected = activeTool === null && (
              item.kind === 'current'
                ? selectedArchiveId === null
                : selectedArchiveId === item.id
            );
            const fullLabel = item.kind === 'current'
              ? `Current: ${item.title}`
              : `Previous chat: ${item.title}`;
            const visibleLabel = item.kind === 'current' ? 'Current' : item.title;
            const signal = item.kind === 'current' ? unreadCount : 0;
            return (
              <button
                key={`${item.kind}:${item.id}`}
                type="button"
                role="tab"
                aria-selected={conversationSelected}
                aria-label={fullLabel}
                title={item.title}
                className={`command-conversation-tab accent-${index % 4}${conversationSelected ? ' active' : ''}`}
                data-kind={item.kind}
                data-testid={item.kind === 'current'
                  ? 'main-command-current-chat'
                  : `main-command-recent-${item.id}`}
                onClick={() => selectConversation(item)}
                onKeyDown={(event) => selectByKeyboard(event, index)}
              >
                <span className="command-conversation-tab-label">{visibleLabel}</span>
                {signal > 0 && (
                  <span className="command-conversation-attention" aria-label={`${signal} unread items`}>
                    {signal > 9 ? '9+' : signal}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <nav className="command-conversation-stationary" aria-label="Main Command tools">
          <button
            type="button"
            aria-pressed={activeTool === 'attention'}
            aria-controls="main-command-organizer-tool-panel"
            className={activeTool === 'attention' ? 'active' : ''}
            onClick={() => onTool(activeTool === 'attention' ? null : 'attention')}
            aria-label="Open Management Attention"
          >
            <span>Attention</span>
            {attentionCount > 0 && <span className="count">{attentionCount}</span>}
          </button>
          <button
            type="button"
            aria-pressed={activeTool === 'archive'}
            aria-controls="main-command-organizer-tool-panel"
            className={activeTool === 'archive' ? 'active' : ''}
            onClick={() => onTool(activeTool === 'archive' ? null : 'archive')}
            aria-label="Open Main Command Archive"
          >
            <span>Archive</span>
          </button>
          <button
            type="button"
            className="command-conversation-new"
            data-testid="main-command-new-chat"
            aria-label="Archive current conversation and start a new chat"
            title="Archive current conversation and start a new chat"
            disabled={!canStartNew || busy}
            onClick={() => {
              onTool(null);
              onNewChat();
            }}
          >
            <span aria-hidden="true">+</span>
            <span>New</span>
          </button>
        </nav>
      </div>

      {activeTool !== null && (
        <section
          id="main-command-organizer-tool-panel"
          ref={toolPanelRef}
          tabIndex={-1}
          className="command-conversation-tool-panel"
          data-tool={activeTool}
          aria-label={activeTool === 'attention' ? 'Management Attention view' : 'Main Command Archive view'}
        >
          {activeTool === 'attention'
              ? (managementAttention ?? <p className="muted">Nothing needs management attention right now.</p>)
              : (
              <div className="command-conversation-archive-list" aria-label="Archived Main Command conversations">
                {archived.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={selectedArchiveId === item.id ? 'active' : ''}
                    aria-current={selectedArchiveId === item.id ? 'page' : undefined}
                    onClick={() => selectConversation(item)}
                  >
                    <strong>{item.title}</strong>
                    <small>Read only · open / continue</small>
                  </button>
                ))}
                {archived.length === 0 && <small className="muted">No archived chats yet.</small>}
              </div>
            )}
        </section>
      )}
    </section>
  );
}
